import { useState, useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import SearchBar from "./SearchBar";
import LoginModal from "./LoginModal";
import CategoryDropdown from "./CategoryDropdown";
import LoginDropdown from "./LoginDropdown";
import MessageModal from "./MessageModal";
import { API_BASE_URL, DASHBOARD_URL } from "../config";

export default function Navbar() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isMessageOpen, setIsMessageOpen] = useState(false);
    const [categories, setCategories] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();
    const { isLoggedIn, userName, userRole, login, logout } = useContext(AuthContext);

    const isAdmin = isLoggedIn && ["admin", "head office"].includes(userRole?.toLowerCase().trim());

    // Fetch categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/public/listing-categories`);
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data || []);
                }
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, []);

    const handleLoginOpen = () => setIsLoginOpen(true);
    const handleLoginClose = () => setIsLoginOpen(false);

    const handleLoginSuccess = async ({ name, role, token }) => {
        login({ token, userName: name, role });
        setIsLoginOpen(false);

        // Fetch messages on login
        try {
            const res = await fetch(`${API_BASE_URL}/api/messages/pending`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();

            if (res.ok && Array.isArray(data) && data.length > 0) {
                setMessages(data);
                setIsMessageOpen(true);
            }
        } catch (err) {
            console.error("Failed to fetch messages:", err);
        }
    };

    const handleLinkClick = () => setMenuOpen(false);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <>
            <header className="sticky top-0 z-50 bg-primary text-white">
                <div className="relative container mx-auto px-6 py-2 flex justify-between items-center z-50">
                    {/* Logo */}
                    <Link
                        to="/"
                        className="transition duration-300 ease-in-out hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.5)]"
                    >
                        <img
                            src="/logo.png"
                            alt="Benecke Group Logo"
                            className="h-[60px] w-auto max-h-[60px] object-contain"
                        />
                    </Link>

                    {/* Nav Links (desktop) */}
                    <nav className="hidden md:flex space-x-4 items-center relative z-50">
                        <Link to="/" className="hover:underline whitespace-nowrap">Home</Link>
                        <Link to="/people" className="hover:underline whitespace-nowrap">The Beneckes</Link>
                        {categories.map((category) => (
                            <CategoryDropdown key={category.id} category={category} />
                        ))}
                        <Link to="/dining" className="whitespace-nowrap">Dining</Link>
                        <Link to="/contact" className="whitespace-nowrap">Contact</Link>
                        <Link to="/careers" className="whitespace-nowrap">Careers</Link>
                        {isAdmin && <Link to="/admin/messages" className="whitespace-nowrap">Admin Messages</Link>}
                    </nav>

                    {/* Search + Login */}
                    <div className="hidden md:flex items-center space-x-4">
                        {/* <input
                            type="text"
                            placeholder="Search..."
                            className="px-3 py-1 rounded-full text-[#1D4E80]"
                        /> */}

                        {!isLoggedIn ? (
                            <a
                                href={DASHBOARD_URL}
                                className="bg-[#f3a648] text-primary px-4 py-1 rounded-full"
                            >
                                Login
                            </a>
                        ) : (
                            <LoginDropdown username={userName} onLogout={handleLogout} />
                        )}
                    </div>

                    {/* Mobile hamburger */}
                    <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden">☰</button>
                </div>

                {/* Mobile Menu */}
                {menuOpen && (
                    <div className="md:hidden px-6 pb-4 space-y-3">
                        {!isLoggedIn ? (
                            <button onClick={() => { handleLoginOpen(); handleLinkClick(); }}>Login</button>
                        ) : (
                            <LoginDropdown username={userName} onLogout={handleLogout} />
                        )}
                        <SearchBar />
                    </div>
                )}
            </header>

            <LoginModal
                isOpen={isLoginOpen}
                onClose={handleLoginClose}
                onLoginSuccess={handleLoginSuccess}
            />
        </>
    );
}
